import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { OrdersConfig, ProductsConfig, Project } from '../models/project';
import { GlobalVariable } from '../helpers/settings';
import { Result } from '../models/general';
import { RefreshVerificationService } from './refresh-verification.service';
import { PaginationOption } from '../models/pagination.model';
import { decrypt, encrypt } from '../helpers/crypto-js';
import { Router } from '@angular/router';

export interface PaginationData {
	docs: Project[];
	total: number;
	limit: number;
	page: number;
	pages: number;
}

@Injectable({
	providedIn: 'root'
})
export class ProjectService {
	readonly BASE_URL = `${GlobalVariable.server.host}/projects`;
	projectSubject: BehaviorSubject<Project | null> = new BehaviorSubject<Project | null>(null);
	public projects: BehaviorSubject<Project[]> = new BehaviorSubject<Project[]>([]);

	constructor(
		private httpClient: HttpClient,
		public router: Router,
		private refreshVerifService: RefreshVerificationService
	) {}

	find(
		isChildComponent: boolean,
		fromConfigComponent: boolean,
		fromAppComponent: boolean,
		globalComponentName: string,
		childComponentName: string,
		route: string,
		projectId?: string,
		action?: string,
		attributes: string = null
	): Observable<Partial<Project[]>> {
		let params;
		if (isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName)
				.set('actionName', action || '');
		}
		if (fromConfigComponent) {
			params = new HttpParams()
				.set('fromConfigComponent', fromConfigComponent)
				.set('componentRoute', route)
				.set('actionName', action || '');
		} else if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId || '')
				.set('actionName', action || '');
		}
		if (!isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('fromConfigComponent', fromConfigComponent)
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('actionName', action || '');
		}
		if (isChildComponent && fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('fromAppComponent', fromAppComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName)
				.set('projectId', projectId || '');
		}
		// params = params.set('attributes', attributes);
		return this.refreshVerifService
			.buidResult<Partial<Project[]>>(
				this.httpClient.get<{ result: Partial<Project[]>; refresh: boolean }>(`${this.BASE_URL}/find`, {
					params
				})
			)
			.pipe(
				tap((res: any) => {
					if (Array.isArray(res)) {
						this.projects.next(res);
					}
				})
			);
	}

	getProjects(
		isChildComponent: boolean = false,
		fromConfigComponent: boolean = false,
		fromAppComponent: boolean = false,
		globalComponentName: string = '',
		childComponentName: string = '',
		route: string = 'guard',
		projectId?: string,
		action: string = 'find projects'
	): Observable<Project[]> {
		if (this.projects.value && this.projects.value.length > 0) {
			return of(this.projects.value);
		}
		return this.find(
			isChildComponent,
			fromConfigComponent,
			fromAppComponent,
			globalComponentName,
			childComponentName,
			route,
			projectId,
			action
		) as Observable<Project[]>;
	}

	getProjectByIndex(
		query: any,
		isChildComponent: boolean,
		fromConfigModelComponent: boolean,
		fromAppComponent: boolean,
		globalComponentName: string,
		childComponentName: string,
		route: string,
		projectId: string,
		action: string
	): Observable<Project> {
		let params;
		if (isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName)
				.set('actionName', action || '');
		}
		if (fromConfigModelComponent) {
			params = new HttpParams()
				.set('fromConfigComponent', fromConfigModelComponent)
				.set('componentRoute', route)
				.set('actionName', action || '');
		} else if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId || '')
				.set('actionName', action || '');
		}
		return this.refreshVerifService.buidResult<Project>(
			this.httpClient.post<Project>(`${this.BASE_URL}/find-by-index`, { query }, { params })
		);
	}

	findByPagination(
		paginationOption: PaginationOption,
		isChildComponent: boolean,
		fromConfigComponent: boolean,
		fromAppComponent: boolean,
		globalComponentName: string,
		childComponentName: string,
		route: string,
		projectId: string,
		action: string
	): Observable<PaginationData> {
		let params;
		if (isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName)
				.set('actionName', action || '');
		}
		if (fromConfigComponent) {
			params = new HttpParams()
				.set('fromConfigComponent', fromConfigComponent)
				.set('componentRoute', route)
				.set('actionName', action || '');
		} else if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId || '')
				.set('actionName', action || '');
		}
		if (!isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('fromConfigComponent', fromConfigComponent)
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('actionName', action || '');
		}
		if (isChildComponent && fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('fromAppComponent', fromAppComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName)
				.set('projectId', projectId || '');
		}
		return this.refreshVerifService.buidResult<PaginationData>(
			this.httpClient.post<{ result: Partial<PaginationData>; refresh: boolean }>(
				`${this.BASE_URL}/find-by-pagination`,
				{ paginationOption },
				{ params }
			)
		);
	}

	findById(
		projectId: string,
		isChildComponent: boolean,
		fromAppComponent: boolean,
		globalComponentName: string,
		childComponentName: string,
		route,
		action: string
	): Observable<Project> {
		let params;
		if (isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName);
		}
		if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId)
				.set('actionName', action);
		}
		if (!isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId)
				.set('actionName', action);
		}
		if (isChildComponent && fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('fromAppComponent', fromAppComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName)
				.set('projectId', projectId);
		}
		return this.refreshVerifService.buidResult<Project>(
			this.httpClient.get<Project>(`${this.BASE_URL}/find-by-id/${projectId}`, { params })
		);
	}

	create(
		project: Partial<Project>,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService
			.buidResult<Result>(this.httpClient.post<Result>(`${this.BASE_URL}/create`, project, { params }))
			.pipe(
				tap(res => {
					if (res) {
						this.clearCache();
					}
				})
			);
	}

	clearCache() {
		this.projects.next([]);
	}

	edit(
		project: Partial<Project>,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult<Result>(
			this.httpClient.put<Result>(`${this.BASE_URL}/edit`, project, { params })
		);
	}
	editMultiple(
		modelCharts: Partial<Project>[],
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult<Result>(
			this.httpClient.put<Result>(`${this.BASE_URL}/edit-multiple`, modelCharts, { params })
		);
	}

	delete(
		projectId: string,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult<Result>(
			this.httpClient.delete<Result>(`${this.BASE_URL}/delete/${projectId}`, { params })
		);
	}

	archive(
		productId: string,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult<Result>(
			this.httpClient.delete<Result>(`${this.BASE_URL}/archive/${productId}`, { params })
		);
	}

	isUsed(
		productId: string,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<Result>(`${this.BASE_URL}/if-is-Used/${productId}`, { params })
		);
	}

	updateConfig(
		data: { project: string; ordersConfig: OrdersConfig },
		fromAppComponent: boolean,
		route: string,
		action: string
	): Observable<Result> {
		let params;
		if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', data.project)
				.set('actionName', action);
		} else {
			params = new HttpParams().set('fromAppComponent', fromAppComponent);
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.put<Result>(`${this.BASE_URL}/orders-config/${data.project}`, data, { params })
		);
	}
	updateProductConfig(data: { project: string; productsConfig: ProductsConfig }): Observable<Result> {
		return this.refreshVerifService.buidResult(
			this.httpClient.put<Result>(`${this.BASE_URL}/products-config/${data.project}`, data)
		);
	}

	getOrdersConfig(
		project: string,
		fromAppComponent: boolean,
		route: string,
		action: string
	): Observable<OrdersConfig> {
		let params;
		if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', project)
				.set('actionName', action);
		} else {
			params = new HttpParams().set('fromAppComponent', fromAppComponent);
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.get<OrdersConfig>(`${this.BASE_URL}/orders-config/${project}`, { params })
		);
	}
	getProductsConfig(project: string): Observable<ProductsConfig> {
		return this.refreshVerifService.buidResult(
			this.httpClient.get<ProductsConfig>(`${this.BASE_URL}/products-config/${project}`)
		);
	}

	setCurrentProject(project: Project | Partial<Project>, refresh = true): any {
		const encryptedProject = encrypt(JSON.stringify(project));
		localStorage.setItem('project', JSON.stringify(encryptedProject));
		if (refresh) {
			this.router.navigate(['/front/dashboard/' + project._id]).then(r => window.location.reload());
		}
	}
	deleteCurrentProject(): any {
		localStorage.removeItem('project');
	}

	getCurrentProject(): Project {
		const project = localStorage.getItem('project');
		if (!project?.length) return;
		let projectObject = JSON.parse(project);
		return JSON.parse(decrypt(projectObject));
	}
	setCurrentProjectProductsTax(config) {
		const projectHashed = localStorage.getItem('project');
		if (!projectHashed) return;
		let projectObject = JSON.parse(projectHashed);
		const project = JSON.parse(decrypt(projectObject));
		project.productsTaxConfig = config;
		this.setCurrentProject(project, false);
	}
	setCurrentProjectProductsConfig(config) {
		const projectHashed = localStorage.getItem('project');
		if (!projectHashed) return;
		let projectObject = JSON.parse(projectHashed);
		const project = JSON.parse(decrypt(projectObject));
		project.productsConfig = config;
		this.setCurrentProject(project, false);
	}
}
